import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { Button } from '@ev-tech/ui-core-components/Button';
import { AiStarGradient } from '@ev-tech/search-modules-shared/Icons';

import { SkillMessage } from '../SkillMessage/SkillMessage';
import { ChatbotWelcome } from '../ChatbotWelcome/ChatbotWelcome';
import { resolveSkills } from '../../utils/conversation';
import { useChatbotStore } from '../../stores/useChatbot';
import { useChatbotSession } from '../../providers/ChatbotProvider';

import styles from './ChatbotConversation.module.css';
import accessibilityStyles from '../../styles/accessibility.module.css';

type ChatbotConversationProps = {
  suggestions: string[];
  isPending: boolean;
  open: boolean;
  shouldFollowRef: MutableRefObject<boolean>;
  onSendMessage: (message: string) => void;
  onRetry: () => void;
};

const AnimatedLoadingText = () => {
  const [dots, setDots] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((dots) => (dots + 1) % 4);
    }, 500);

    return () => clearInterval(interval);
  }, []);

  return <span aria-hidden="true">{'.'.repeat(dots)}</span>;
};

export const ChatbotConversation = ({
  suggestions,
  isPending,
  open,
  shouldFollowRef,
  onSendMessage,
  onRetry,
}: ChatbotConversationProps) => {
  const { t } = useChatbotSession();
  const conversation = useChatbotStore((state) => state.conversation);
  const retryMessageId = useChatbotStore((state) => state.retryMessageId);
  const pageSkills = useChatbotStore((state) => state.pageSkills);
  const skills = resolveSkills(pageSkills);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (shouldFollowRef.current && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [conversation, isPending, open, shouldFollowRef]);

  return (
    <div
      className={styles.scrollArea}
      ref={scrollRef}
      onScroll={() => {
        const element = scrollRef.current;

        if (element) shouldFollowRef.current = element.scrollHeight - element.scrollTop - element.clientHeight < 48;
      }}
    >
      {conversation.length === 0 ? (
        <ChatbotWelcome suggestions={suggestions} disabled={isPending} onSendMessage={onSendMessage} />
      ) : (
        <div
          role="log"
          aria-label={t('chatbot.conversation')}
          aria-live="polite"
          aria-relevant="additions text"
          className={styles.conversation}
        >
          {conversation.map((message) => (
            <div key={message.id} className={styles.message} data-author={message.messageType}>
              <span className={accessibilityStyles.srOnly}>
                {message.messageType === 'USER' ? t('chatbot.user') : t('chatbot.assistant')}
              </span>
              <Copy id={`chatbot-message-${message.id}`} size="l" className={styles.messageText} text={message.message} />
              {message.messageType === 'CHATBOT' && (
                <div className={styles.skill}>
                  <SkillMessage message={message} skill={skills.get(message.skillName ?? '')} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {isPending && (
        <div role="status">
          <Copy
            className={styles.loading}
            size="m"
            text={
              <>
                <AiStarGradient aria-hidden="true" />
                {t('chatbot.loading')}&nbsp;
                <AnimatedLoadingText />
              </>
            }
          />
        </div>
      )}
      {!isPending && retryMessageId && (
        <div role="alert">
          <Copy size="m" text={t('chatbot.requestFailed')} />
          <Button label={t('chatbot.retry')} size="small" variant="secondary" onClick={onRetry} />
        </div>
      )}
    </div>
  );
};
